/**
 * AppBar Help control (DNKADM-0376). Replaces the dead question-mark button
 * with a menu that shows the compiled build stamp (`getBuildId()`) and Copy.
 * Development builds (no compiled id) still open the menu so the control is
 * not dead. Must stay a descendant of `ToastProvider` — do not nest another.
 *
 * DNKADM-0382 — the menu also says whether this build is current, on demand:
 *  - U1: a readable build date, in words, ABOVE the identifier — read from the stamp's
 *    own `YYYYMMDD` prefix (`parseDeployStampDate`), never a second source. A `local-`
 *    build or an unidentifiable one shows no date row. The identifier row and the Copy
 *    row are unchanged; Copy still copies the identifier.
 *  - U2: opening the menu runs the shared build check (`checkBuildCurrency`) and reports
 *    one of three states — checking, up to date, or a new version is available with a
 *    Reload action (the same `reloadForNewBuild` the snackbar uses). Any failure shows
 *    no currency row at all; the stamp and Copy still render. No check fires on mount,
 *    on a timer, or for an unidentifiable build.
 *  - U3: one fetch, one comparison, shared with `StaleBuildNotice`. Opening the menu
 *    does not touch the snackbar's dismiss hold, and a dismissed snackbar does not
 *    suppress the menu's answer.
 */
import { ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { useRef, useState, type MouseEvent } from 'react';
import { IconAction } from '@/components/IconAction';
import { getBuildId } from '@/config/buildId';
import { parseDeployStampDate } from '@/config/deployStamp';
import { Icon, MONO } from '@/theme';
import { checkBuildCurrency, reloadForNewBuild, type BuildCurrency } from './buildCheck';
import { useToast } from './useToast';
import { longMonthDateLabel } from '@/utils/clockTime';

const COPY_ERROR = "Couldn't copy the build stamp. Check clipboard permissions and try again.";

/** The menu's currency row: `null` renders no row (never checked, or the check failed). */
type CurrencyState = 'checking' | BuildCurrency | null;

/**
 * The build date in words (e.g. "September 7, 2026"), from the stamp's own digits
 * (DNKADM-0476 F-05). `parseDeployStampDate` returns the stamp's calendar day as a DEVICE-local
 * midnight, so it is read back with the device getters and never rendered in another zone (a
 * venue west of the device would show the day before).
 */
function formatBuildDate(date: Date): string {
  const ymd = `${String(date.getFullYear()).padStart(4, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  return longMonthDateLabel(ymd) ?? ymd;
}

export function HelpMenu() {
  const showToast = useToast();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [currency, setCurrency] = useState<CurrencyState>(null);
  // Each open starts a new check; a result from an earlier open never lands on a later one.
  const checkSeqRef = useRef(0);
  const buildId = getBuildId();
  const buildDate = buildId === null ? null : parseDeployStampDate(buildId);
  const open = Boolean(anchor);

  function handleOpen(event: MouseEvent<HTMLElement>) {
    setAnchor(event.currentTarget);
    if (buildId === null) {
      // Unidentifiable build: no check, no currency row.
      setCurrency(null);
      return;
    }
    const seq = checkSeqRef.current + 1;
    checkSeqRef.current = seq;
    setCurrency('checking');
    void checkBuildCurrency().then((result) => {
      if (checkSeqRef.current !== seq) return;
      setCurrency(result);
    });
  }

  function handleClose() {
    setAnchor(null);
  }

  async function handleCopy() {
    const stamp = getBuildId();
    if (stamp === null) return;
    const clipboard = navigator.clipboard;
    if (clipboard === undefined || typeof clipboard.writeText !== 'function') {
      showToast(COPY_ERROR, 'error');
      return;
    }
    try {
      await clipboard.writeText(stamp);
    } catch {
      showToast(COPY_ERROR, 'error');
      return;
    }
    handleClose();
    showToast('Build stamp copied.');
  }

  return (
    <>
      <IconAction
        label="Help"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={handleOpen}
        sx={{ width: 44, height: 44 }}
      >
        <Icon name="help" sx={{ fontSize: 22 }} />
      </IconAction>
      <Menu anchorEl={anchor} open={open} onClose={handleClose}>
        {buildId !== null ? (
          <>
            {buildDate !== null && (
              <MenuItem disabled>
                <ListItemText primary={`Built ${formatBuildDate(buildDate)}`} secondary="Build date" />
              </MenuItem>
            )}
            <MenuItem disabled>
              <ListItemText
                primary={buildId}
                secondary="Build"
                slotProps={{ primary: { sx: { fontFamily: MONO } } }}
              />
            </MenuItem>
            <MenuItem onClick={() => void handleCopy()}>
              <ListItemIcon>
                <Icon name="content_copy" sx={{ fontSize: 20 }} />
              </ListItemIcon>
              Copy build stamp
            </MenuItem>
            {currency === 'checking' && (
              <MenuItem disabled>
                <ListItemIcon>
                  <Icon name="sync" sx={{ fontSize: 20 }} />
                </ListItemIcon>
                <ListItemText primary="Checking for a newer version…" />
              </MenuItem>
            )}
            {currency === 'current' && (
              <MenuItem disabled>
                <ListItemIcon>
                  <Icon name="check_circle" sx={{ fontSize: 20 }} />
                </ListItemIcon>
                <ListItemText primary="Up to date" secondary="This is the version being served." />
              </MenuItem>
            )}
            {currency === 'stale' && (
              <MenuItem onClick={reloadForNewBuild}>
                <ListItemIcon>
                  <Icon name="refresh" sx={{ fontSize: 20 }} />
                </ListItemIcon>
                <ListItemText
                  primary="A new version is available"
                  secondary="Reload to update this tab."
                />
              </MenuItem>
            )}
          </>
        ) : (
          <MenuItem disabled>
            <ListItemText primary="Development" />
          </MenuItem>
        )}
      </Menu>
    </>
  );
}
